import React, { useState, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import { RaiseIssueModal } from './RaiseIssueModal';
import './ThresholdAlert.scss';

interface IThresholdAlertProps {
  component: any;
  condition: any;
  currentValue: string | number;
  thresholdValue: string | number;
  formTemplateId?: string;
  onDismiss?: () => void;
  onIssueRaised?: (conditionId: string) => void;
  isIssueRaised?: boolean; // Track if issue has been raised for this condition
}

export const ThresholdAlert: React.FC<IThresholdAlertProps> = ({
  component,
  condition,
  currentValue,
  thresholdValue,
  formTemplateId,
  onIssueRaised,
  isIssueRaised = false
}) => {
  const [showRaiseIssueModal, setShowRaiseIssueModal] = useState(false);

  const getOperatorSymbol = (operator: string): string => {
    const symbols: Record<string, string> = {
      'greaterThan': '>',
      'lessThan': '<',
      'greaterThanOrEqual': '≥',
      'lessThanOrEqual': '≤',
      'equals': '=',
      'notEquals': '≠'
    };
    return symbols[operator] || operator;
  };

  const getConditionMessage = (): string => {
    const operatorSymbol = getOperatorSymbol(condition.operator);
    return `${component.basic?.label || 'Value'}: Value ${currentValue} exceeds threshold ${operatorSymbol} ${thresholdValue}`;
  };

  const handleRaiseIssueClick = useCallback(() => {
    // Open the modal directly - notes and attachments will be handled inside the modal
    setShowRaiseIssueModal(true);
  }, []);

  const handleRaiseIssueClose = useCallback(() => {
    setShowRaiseIssueModal(false);
  }, []);

  const handleRaiseIssueSuccess = useCallback(() => {
    setShowRaiseIssueModal(false);
    // Notify parent that issue has been raised for this condition
    if (onIssueRaised && condition.id) {
      onIssueRaised(condition.id);
    }
  }, [onIssueRaised, condition.id]);

  return (
    <>
      <div className={`threshold-alert ${!isIssueRaised ? 'threshold-alert-unresolved' : 'threshold-alert-resolved'}`}>
        <div className="threshold-alert-border" />
        <div className="threshold-alert-content">
          <div className="threshold-alert-header">
            <div className="threshold-alert-title-group">
              <AlertTriangle className="threshold-alert-icon" size={20} />
              <span className="threshold-alert-title">
                {!isIssueRaised ? 'Threshold Condition Met - Action Required' : 'Threshold Condition Met'}
              </span>
            </div>
          </div>
          
          <div className="threshold-alert-message">
            {getConditionMessage()}
            {!isIssueRaised && (
              <span className="threshold-alert-warning"> You must raise an issue before submitting the form.</span>
            )}
          </div>

          <div className="threshold-alert-buttons">
            <button
              type="button"
              onClick={handleRaiseIssueClick}
              className="threshold-alert-raise-issue-btn"
            >
              {!isIssueRaised ? 'Raise Issue Now' : 'View Issue'}
            </button>
          </div>
        </div>
      </div>

      {showRaiseIssueModal && (
        <RaiseIssueModal
          isOpen={showRaiseIssueModal}
          onClose={handleRaiseIssueClose}
          onSuccess={handleRaiseIssueSuccess}
          component={component}
          formTemplateId={formTemplateId || ''}
          notes=""
          attachments={null}
        />
      )}
    </>
  );
};

